import { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useLogin, auth } from '@field-tickets/shared';
import { Spinner } from '@/components/Spinner';
import { useAuthStore } from '@/store/auth';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function getStringProp(value: unknown, key: string): string {
  if (!isRecord(value)) return '';
  const prop = value[key];
  return typeof prop === 'string' ? prop : '';
}

function getNumberProp(value: unknown, key: string): number | undefined {
  if (!isRecord(value)) return undefined;
  const prop = value[key];
  return typeof prop === 'number' ? prop : undefined;
}

function getAuthErrorDetails(err: unknown): { code: string; message: string; status?: number } {
  const data = isRecord(err) ? err.data : undefined;
  const message = err instanceof Error ? err.message : getStringProp(err, 'message');
  const code =
    getStringProp(err, 'error') ||
    getStringProp(data, 'error') ||
    getStringProp(data, 'message') ||
    message;

  return {
    code,
    message: message || getStringProp(data, 'message') || code,
    status: getNumberProp(err, 'status'),
  };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const loginMutation = useLogin();
  const [googleLoading, setGoogleLoading] = useState(false);

  // Surface error codes from the OAuth callback. The backend redirects to
  // /login?error=<code> for several distinct failure modes — we route the
  // status-bearing ones (no_access, pending_approval, account_suspended) to
  // their dedicated pages and render a friendly inline message for the
  // generic transport-level failures (missing_code, auth_failed, server_error).
  useEffect(() => {
    const errorCode = searchParams.get('error');
    if (!errorCode) return;

    if (errorCode === 'no_access' || errorCode === 'account_suspended') {
      navigate('/no-access', { replace: true });
      return;
    }
    if (errorCode === 'pending_approval') {
      navigate('/pending-approval', { replace: true });
      return;
    }

    const messages: Record<string, string> = {
      missing_code: 'We did not receive a sign-in code from Google. Please try again.',
      auth_failed: 'Google sign-in failed to verify your account. Please try again.',
      server_error: 'A server error occurred while completing sign-in. Please try again.',
    };
    setError(messages[errorCode] || 'Sign-in failed. Please try again.');
  }, [searchParams, navigate]);

  useEffect(() => {
    const invitedEmail = searchParams.get('invite');
    if (!invitedEmail) return;
    setEmail(invitedEmail);
    setError('');
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const result = await loginMutation.mutateAsync({ email, password });
      if (result.success || result.token) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (err: unknown) {
      const { code, message } = getAuthErrorDetails(err);
      if (code === 'pending_approval') {
        navigate('/pending-approval', { replace: true });
        return;
      }
      // no_access and account_suspended both route to /no-access so all three
      // entry paths (password login, OAuth callback, password reset) agree on
      // where a blocked account lands (audit qf-6 consistency fix — the OAuth
      // and reset paths already redirect account_suspended there).
      if (code === 'no_access' || code === 'account_suspended') {
        navigate('/no-access', { replace: true });
        return;
      }
      setError(message || 'Login failed');
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const result = await auth.signInWithGoogle();
      if (result.url) {
        window.location.href = result.url;
        return;
      }
      setError('Google sign-in is not available');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Google sign-in failed';
      setError(msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  const isLoading = loginMutation.isPending || googleLoading;

  if (isAuthenticated && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
          <img src="/alturex-logo.svg" alt="Alturex Communications" className="login-logo" />
          <h1 className="login-title">Alturex Work</h1>
          <div className="login-brand">Tickets, sales, catalog, and customer work</div>
        </div>

        <p className="login-subtitle" style={{ textAlign: 'center' }}>
          Sign in to your Alturex workspace
        </p>

        {error && (
          <div style={{
            color: 'var(--color-error)',
            marginBottom: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
            textAlign: 'center',
            background: 'rgba(222, 53, 11, 0.08)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              autoComplete="current-password"
              minLength={8}
            />
          </div>

          <div style={{ textAlign: 'right', marginBottom: 'var(--space-3)' }}>
            <Link
              to="/forgot-password"
              style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className="login-btn"
            disabled={isLoading}
            style={{ marginBottom: 'var(--space-3)' }}
          >
            {isLoading ? <Spinner size="sm" /> : 'Sign In'}
          </button>
        </form>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          margin: 'var(--space-4) 0',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--text-sm)',
        }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border-default)' }} />
          <span>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border-default)' }} />
        </div>

        <button
          type="button"
          className="login-btn"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          style={{ background: '#4285f4', marginBottom: 'var(--space-4)' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Sign in with Google
        </button>

        {/* #2103: no self-signup. Access is granted by an Ayrical invite. */}
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', textAlign: 'center', margin: 0 }}>
          No account? People are invited from Ayrical. Ask an admin to invite you.
        </p>
      </div>
    </div>
  );
}
